import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Phase } from '../../../projects/domain/phase';
import { PhaseWithEmployees } from '../../../projects/domain/phase-with-employees';
import { BehaviorSubject } from 'rxjs';
import { EmployeeConflict } from '../../models/employee-conflict';
import { take } from 'rxjs/operators';
import { AssignEmployeeService } from '../../../services/assign-employee.service';

@Component({
  selector: 'app-phases-step',

  standalone: false,
  template: `
    <div class="fx fx--col fx-fill">
      <header class="header text-center">
        <button mat-icon-button class="close-button" (click)="onEmployeeAssignClose()">
          <mat-icon svgIcon="close"></mat-icon>
        </button>
        <p class="text--gray text-20-500">To which phase assign the employee(s)?</p>
      </header>

      <div class="bg-gray fx-grow">
        <div class="container-details">
          <mat-card>
            <mat-card-content>
              <form [formGroup]="form">
                <mat-radio-group
                  class="fx fx--col fx__gap--16 radio-button-phases"
                  formControlName="phase"
                  (change)="checkForConflicts($event.value)"
                >
                  <mat-radio-button
                    color="primary"
                    *ngFor="let phase of phases; index as i "
                    [value]="phase"
                  >
                    <app-phase-radio-button
                      [phase]="phase"
                      [index]="i"
                      [phaseWithEmployees]="getPhaseWithEmployees(phase)"
                    ></app-phase-radio-button>
                  </mat-radio-button>
                </mat-radio-group>
                <p class="text-center text--light-gray" *ngIf="!phases.length">
                  No phases in project
                </p>
              </form>

              <div class="fx fx__just--end fx__align--center">
                <button mat-button *ngIf="isPreviousStep" (click)="goBackToPreviousStep()">Previous</button>
                <button
                  mat-button
                  *ngIf="isNextStep; else assign"
                  [disabled]="!form.valid"
                  (click)="onPhaseSelect()"
                >
                  Next
                </button>
                <ng-template #assign>
                  <button mat-flat-button color="primary" [disabled]="!form.valid" (click)="onPhaseSelect()">
                    Assign
                  </button>
                </ng-template>
              </div>
            </mat-card-content>
          </mat-card>
          <mat-card *ngIf="(conflicts$ | async).length > 0">
            <app-assign-employee-conflicts
              [conflicts]="conflicts$ | async"
            ></app-assign-employee-conflicts>
          </mat-card>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['../employee-assign.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhasesStepComponent implements OnChanges {
  @Input() isPreviousStep: boolean;
  @Input() isNextStep: boolean;
  @Input() projectId: string;
  @Input() phases: Phase[];
  @Input() phasesWithEmployees: PhaseWithEmployees[];
  @Input() employeeIds: string[];

  @Output() closeEmployeeAssign = new EventEmitter<void>();
  @Output() backToPreviousStep = new EventEmitter<void>();
  @Output() phaseSelected = new EventEmitter<PhaseWithEmployees>();

  form = new FormGroup({
    phase: new FormControl<Phase>(undefined, Validators.required),
  });

  readonly conflicts$ = new BehaviorSubject<EmployeeConflict[]>([]);

  constructor(private readonly assignEmployeeService: AssignEmployeeService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['phases']) {
      const [firstPhase] = this.phases ?? [];
      if (firstPhase && !this.form.controls.phase.value) {
        this.form.controls.phase.setValue(firstPhase);
        this.checkForConflicts(firstPhase);
      }
    }

    if (changes['employeeIds']) {
      this.checkForConflicts(this.form.controls.phase.value);
    }
  }

  getPhaseWithEmployees(phase: Phase): PhaseWithEmployees | null {
    if (!this.phasesWithEmployees) return null;
    return this.phasesWithEmployees.find((pwe) => pwe.id === phase.id) || null;
  }

  goBackToPreviousStep(): void {
    this.backToPreviousStep.emit();
  }

  onPhaseSelect(): void {
    const phase = this.form.getRawValue().phase;
    if (!phase) {
      return;
    }

    this.phaseSelected.emit(this.getPhaseWithEmployees(phase) ?? this.toPhaseWithEmployees(phase));
  }

  onEmployeeAssignClose(): void {
    this.closeEmployeeAssign.emit();
  }

  checkForConflicts(phase: Phase): void {
    if (!this.employeeIds || !phase) return;

    this.assignEmployeeService.checkConflictsWithProject(this.projectId, phase.id, this.employeeIds)
      .pipe(take(1))
      .subscribe({
        next: () => this.conflicts$.next([]),
        error: (error) => this.conflicts$.next(error.error),
      });
  }

  private toPhaseWithEmployees(phase: Phase): PhaseWithEmployees {
    return {
      id: phase.id,
      phaseNameId: phase.phaseNameId,
      phaseName: phase.phaseName,
      isA1: phase.isA1,
      startDate: phase.startDate,
      endDate: phase.endDate,
      assigned: 0,
      required: 0,
      conflicts: 0,
      professionCounters: [],
      employees: [],
      requiredProfessions: phase.requiredProfessions,
      requiredVisas: phase.requiredVisas,
    };
  }
}
